import { Link } from 'react-router-dom';
import './Home.css';

export default function About() {
  return (
    <div className="jp-root">

      {/* Hero */}
      <section className="jp-section" style={{ paddingTop: 64, paddingBottom: 48, borderBottom: '1px solid var(--jp-gray-200)' }}>
        <span className="jp-label">about</span>
        <h1 className="jp-section-title">About <strong>Jamasp</strong></h1>
        <p className="jp-section-sub">
          A prototype research platform for collecting continuous physiological data
          from existing wearable device owners, recruited via crowdsourcing platforms.
        </p>
      </section>

      {/* What is Jamasp */}
      <section className="jp-section">
        <span className="jp-label">overview</span>
        <h2 className="jp-section-title">What is Jamasp?</h2>
        <p className="jp-section-sub" style={{ maxWidth: 680 }}>
          Most wearable-based research relies on lab-issued devices, small convenience samples,
          or large institutional cohorts. Jamasp offers a complementary approach: ad hoc recruitment
          of existing device owners through crowdsourcing platforms, enabling longitudinal physiological
          data collection at a fraction of the infrastructure cost — combined with the flexibility to
          target specific populations, time windows, and research designs.
        </p>
        <p className="jp-section-sub" style={{ maxWidth: 680, marginTop: 16 }}>
          Jamasp currently integrates Fitbit devices, through the Google Health API, with Prolific, an
          online participant recruitment platform, via secure OAuth-based authentication. Data are automatically retrieved and stored
          in a structured, queryable BigQuery database, accessible via a researcher-facing dashboard.
        </p>
      </section>

      <hr className="jp-divider" />

      {/* How it works */}
      <section className="jp-section">
        <span className="jp-label">workflow</span>
        <h2 className="jp-section-title">How it works</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 680, marginTop: 8 }}>
          {[
            { step: '01', title: 'Recruit', desc: 'A study link is posted on Prolific. Researchers can filter by device ownership, demographics, and other criteria. Only eligible participants see the study.' },
            { step: '02', title: 'Consent & Register', desc: 'Participants complete a brief onboarding wizard, review study information, and consent to data sharing — median completion time: 2.6 minutes.' },
            { step: '03', title: 'Authenticate', desc: 'Participants sign in with their Google account and authorize Google Health access to specific data streams. No new accounts or payment channels are required.' },
            { step: '04', title: 'Fetch & Store', desc: 'Jamasp automatically retrieves data on a researcher-defined schedule and stores it in a structured, queryable BigQuery database.' },
            { step: '05', title: 'Monitor & Analyze', desc: 'Researchers access a dashboard for real-time monitoring, visualization, and exploratory analysis using Google Cloud tools.' },
          ].map(({ step, title, desc }) => (
            <div key={step} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <span className="jp-mono" style={{ fontSize: 12, color: 'var(--jp-blue-mid)', minWidth: 28, paddingTop: 2 }}>{step}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: 'var(--jp-gray-800)' }}>{title}</div>
                <div style={{ fontSize: 14, color: 'var(--jp-gray-600)', lineHeight: 1.7 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <hr className="jp-divider" />

      {/* Data streams */}
      <section className="jp-section">
        <span className="jp-label">data streams</span>
        <h2 className="jp-section-title">Supported Fitbit streams</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, maxWidth: 680, marginTop: 16 }}>
          {[
            'Heart rate (minute-level)',
            'Steps (minute-level)',
            'Calories & distances',
            'Floors & elevation',
            'Sleep stages (episode-level)',
            'Heart rate variability (HRV)',
            'Blood oxygen (SpO₂)',
            'Skin temperature',
            'Breathing rate',
            'VO₂ max estimate',
          ].map(stream => (
            <div key={stream} style={{ fontSize: 13, color: 'var(--jp-gray-600)', padding: '8px 12px', background: 'var(--jp-gray-50)', borderRadius: 6, border: '1px solid var(--jp-gray-200)' }}>
              {stream}
            </div>
          ))}
        </div>
      </section>

      <hr className="jp-divider" />

      {/* Pilot study */}
      <section className="jp-section">
        <span className="jp-label">pilot study</span>
        <h2 className="jp-section-title">Feasibility study</h2>
        <p className="jp-section-sub" style={{ maxWidth: 680 }}>
          Jamasp was pilot-tested recruiting N = 41 Fitbit owners via Prolific between February and
          April 2025. Participants completed registration in a median of 2.6 minutes, and data were
          collected across 9 physiological streams over a 65-day window. Results indicate that
          recruiting existing wearable owners via crowdsourcing and linking them to an automated
          data pipeline is a viable, low-friction approach to scalable physiological data collection.
        </p>
        <div className="jp-paper-cite" style={{ maxWidth: 680, marginTop: 24 }}>
          <div className="jp-paper-cite-label">under review</div>
          <div className="jp-paper-cite-title">
            Wearable Device Data Collection From Existing Device Owners via Crowdsourcing Platforms: A Feasibility Study Using Jamasp
          </div>
          <div className="jp-paper-cite-authors">
            Mostafa Salari Rad · Arman Radmanesh · New School for Social Research &amp; University of Oklahoma
          </div>
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
          <a href="https://github.com/cutsocial/jamasp-public" target="_blank" rel="noreferrer" className="jp-btn jp-btn-outline">
            View code &amp; data →
          </a>
          <Link to="/explore" className="jp-btn jp-btn-primary">
            Explore pilot data →
          </Link>
        </div>
      </section>

        {/* Footer */}
        <footer className="jp-footer">
          <nav className="jp-footer-links">
            <Link to="/about">About</Link>
            <a href="https://github.com/cutsocial/jamasp-public" target="_blank" rel="noreferrer">GitHub</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="mailto:salarirad@newschool.edu">Contact</a>
          </nav>
          <span className="jp-footer-copy">© Jamasp 2026</span>
        </footer>

    </div>
  );
}
